import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Trophy, Target, Flame, X } from 'lucide-react';
import { useGameStore } from '../store/gameStore';
import { ACHIEVEMENTS } from '../services/achievements';
import Button from '../components/common/Button';
import { Card } from '@/components/ui/card';

export default function Statistics() {
  const navigate = useNavigate();
  const { currentProfile, levelProgress, resetProgress } = useGameStore();

  const getTotalStars = () => {
    let total = 0;
    levelProgress.forEach(progress => {
      total += progress.stars;
    });
    return total;
  };

  const getTotalScore = () => {
    let total = 0;
    levelProgress.forEach(progress => {
      total += progress.score;
    });
    return total;
  };

  const getBestStreak = () => {
    let best = 0;
    levelProgress.forEach(progress => {
      if (progress.bestStreak > best) {
        best = progress.bestStreak;
      }
    });
    return best;
  };

  const getCompletedLevels = () => {
    return levelProgress.size;
  };

  const handleReset = () => {
    if (confirm('Are you sure you want to reset all progress? This cannot be undone!')) {
      resetProgress();
    }
  };

  if (!currentProfile) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen p-4 relative">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-4xl font-bold text-white">Your Progress</h1>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
        >
          <Card className="p-6 bg-gradient-to-r from-purple-900/50 to-pink-900/50 backdrop-blur border-white/20">
            <div className="flex items-center gap-6">
              <div 
                className="w-24 h-24 rounded-full flex items-center justify-center"
                style={{ backgroundColor: currentProfile.color }}
              >
                <img src={currentProfile.avatar} alt={currentProfile.name} className="w-20 h-20" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">{currentProfile.name}</h2>
                <p className="text-gray-400">
                  Space Cadet since {new Date(currentProfile.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </Card>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-6 bg-white/10 backdrop-blur border-white/20 text-center">
              <Star className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
              <div className="text-3xl font-bold text-white mb-1">{getTotalStars()}</div>
              <div className="text-gray-400">Total Stars</div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="p-6 bg-white/10 backdrop-blur border-white/20 text-center">
              <Trophy className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <div className="text-3xl font-bold text-white mb-1">{getCompletedLevels()}/10</div>
              <div className="text-gray-400">Levels</div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="p-6 bg-white/10 backdrop-blur border-white/20 text-center">
              <Flame className="w-8 h-8 text-orange-400 mx-auto mb-2" />
              <div className="text-3xl font-bold text-white mb-1">{getBestStreak()}</div>
              <div className="text-gray-400">Best Streak</div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="p-6 bg-white/10 backdrop-blur border-white/20 text-center">
              <Target className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <div className="text-3xl font-bold text-white mb-1">{getTotalScore()}</div>
              <div className="text-gray-400">Total Score</div>
            </Card>
          </motion.div>
        </div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="p-6 bg-white/10 backdrop-blur border-white/20">
            <h3 className="text-2xl font-bold text-white mb-4">Achievements</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {ACHIEVEMENTS.map((achievement, index) => {
                const isUnlocked = false; // TODO: Check if achievement is unlocked
                
                return (
                  <motion.div
                    key={achievement.id}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.6 + index * 0.05 }}
                  >
                    <div className={`p-4 rounded-lg text-center ${
                      isUnlocked 
                        ? 'bg-gradient-to-br from-yellow-900/50 to-orange-900/50 border border-yellow-500/50' 
                        : 'bg-gray-900/50 border border-gray-700/50'
                    }`}>
                      <div className="text-3xl mb-2">{achievement.icon}</div>
                      <h4 className={`font-semibold mb-1 ${
                        isUnlocked ? 'text-white' : 'text-gray-500'
                      }`}>
                        {achievement.name}
                      </h4>
                      <p className={`text-xs ${
                        isUnlocked ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {achievement.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-8 text-center"
        >
          <Button
            variant="danger"
            size="md"
            onClick={handleReset}
          >
            Reset Progress
          </Button>
        </motion.div>
      </div>
    </div>
  );
}